import type { Schema } from "@/shared/components/Form/types";
import { LINK_TYPE_LABEL_KEYS } from "./config";
import { LINK_REWARD_TYPE_LABEL_KEYS } from "./reward";
import { getLinkTargetField } from "./linkTargets";
import {
  LinkRewardTypeType,
  LinkTypeType,
} from "@/types/graphql/partner.generated";
import type { TFunction } from "i18next";
import type { ReactNode } from "react";

/**
 * Форма ссылки. currency: валюта организации, в ней задаётся фиксированная
 * награда (адорнмент поля суммы); без неё адорнмента нет. liveEnabled:
 * предлагать ли эфир целью ссылки.
 */
export const getLinkSchema = (
  t: TFunction,
  slugAdornment?: ReactNode,
  currency?: string | null,
  liveEnabled = true
): Schema => ({
  title: {
    type: "text",
    label: t("links.form.titleLabel"),
    required: true,
    xs: 12,
    fullWidth: true,
  },
  linkType: {
    type: "radioGroup",
    label: t("links.form.linkTypeLabel"),
    options: Object.entries(LINK_TYPE_LABEL_KEYS).map(([value, key]) => ({
      value,
      label: t(key),
    })),
    row: true,
    xs: 12,
    fullWidth: true,
  },
  href: {
    type: "url",
    label: t("links.form.hrefLabel"),
    required: true,
    xs: 12,
    fullWidth: true,
    visibleCheck: ({ values }) =>
      values?.linkType === LinkTypeType.ExternalType,
  },
  _object: {
    type: "selectObject",
    xs: 12,
    fullWidth: true,
    visibleCheck: ({ values }) =>
      values?.linkType === LinkTypeType.InternalType,
    ...getLinkTargetField(t, { liveEnabled }),
  },
  slug: {
    type: "text",
    label: t("links.form.slugLabel"),
    helperText: t("links.form.slugHelper"),
    xs: 12,
    fullWidth: true,
    startAdornment: slugAdornment,
  },
  utmSource: {
    type: "text",
    label: "UTM Source",
    helperText: t("links.form.utmSourceHelper"),
    xs: 12,
    fullWidth: true,
  },
  utmMedium: {
    type: "text",
    label: "UTM Medium",
    helperText: t("links.form.utmMediumHelper"),
    xs: 12,
    fullWidth: true,
  },
  utmCampaign: {
    type: "text",
    label: "UTM Campaign",
    helperText: t("links.form.utmCampaignHelper"),
    xs: 12,
    fullWidth: true,
  },
  _rewardTitle: {
    type: "title",
    label: t("links.form.rewardTitle"),
    variant: "subtitle1",
    xs: 12,
    fullWidth: true,
  },
  rewardType: {
    type: "radioGroup",
    label: t("links.form.rewardTypeLabel"),
    options: Object.entries(LINK_REWARD_TYPE_LABEL_KEYS).map(
      ([value, key]) => ({ value, label: t(key) })
    ),
    row: true,
    xs: 12,
    fullWidth: true,
  },
  rewardPercent: {
    type: "number",
    label: t("links.form.rewardPercentLabel"),
    helperText: t("links.form.rewardPercentHelper"),
    endAdornment: "%",
    step: 0.01,
    nullable: true,
    xs: 12,
    fullWidth: true,
    visibleCheck: ({ values }) =>
      values?.rewardType === LinkRewardTypeType.PercentType,
  },
  rewardFixedAmount: {
    type: "number",
    label: t("links.form.rewardFixedLabel"),
    helperText: t("links.form.rewardFixedHelper"),
    endAdornment: currency ?? undefined,
    step: 0.01,
    nullable: true,
    xs: 12,
    fullWidth: true,
    visibleCheck: ({ values }) =>
      values?.rewardType === LinkRewardTypeType.FixedType,
  },
});
